import React, { Fragment, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import clsx from 'clsx'

import { Prompt, useLocation } from 'react-router-dom'
import {
  Controller,
  FormContext,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { User as UserIcon, Watch as WatchIcon } from 'react-feather'
import { filter, find, isEmpty, size } from 'lodash'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Alert, AlertTitle, Autocomplete } from '@material-ui/lab'
import {
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'

import { Info as InfoIcon } from '@material-ui/icons'

import {
  CompleteModal,
  ActivityAnalysisDai,
  ActivityAnalysisDaiButton,
  EvidencePlanAction,
} from './components'
import {
  AlertDialog,
  ConfirmationDialog,
  JoditEditor,
  PermittedByAssignmentRole,
  WhiteTooltip,
} from 'components'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'
import useAssignmentPermission from 'hooks/useAssignmentPermission'
import useDaiChat from 'hooks/useDaiChat'
import useAuth from 'hooks/useAuth'

import * as service from 'service'
import constants from 'constants/index'
import schema from './schema'
import theme from 'theme'
import useStyles from './styles'
import DaiGovernanceBanner from '../DaiGovernanceBanner'

const ActivityManageForm = ({
  users = [],
  activity,
  refresh,
  states,
  setStates,
  governanceProjectId,
  activityName,
  activityCode,
}) => {
  const { INITIAL_STATUS_ID, HEADWAY_STATUS_ID } = constants.planAction
  const {
    ARCHIVED_STATUS_ID,
    INACTIVE_STATUS_ID,
    MANAGER_ROLE,
    NOT_APPLICABLE_VALUE,
    OBSERVER_ROLE,
    PLAN_ACTION_RESPONSIBLE_ROLE,
    PROJECT_RESPONSIBLE_ROLE,
  } = constants.governanceProject
  const {
    AWAITING_APPROVAL_STATUS_ID,
    COMPLETED_STATUS_ID,
    IN_PROGRESS_STATUS_ID,
    LEAVE_WITH_PENDING_CHANGES_MESSAGE,
    REJECTED_STATUS_ID,
    RISK_ASSUMED_STATUS_ID,
  } = constants.governanceProjectActivity
  const { RICH_TEXT_SANITIZE_PROFILE } = constants.jodit

  const { defaultValues, loading, planActionsOpened } = states
  const { setLoading, handleOpenAlertDialog } = setStates

  const [isReject, setIsReject] = useState(false)
  const [isSubmitReviewDialogOpen, setIsSubmitReviewDialogOpen] =
    useState(false)
  const [isReopenReviewDialogOpen, setIsReopenReviewDialogOpen] =
    useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedAlternative, setSelectedAlternative] = useState({})
  const [activityDeadlineDate, setActivityDeadlineDate] = useState(
    moment(activity?.deadlineDate),
  )
  const [openDestroyEvidencePlanAction, setOpenDestroyEvidencePlanAction] =
    useState(false)
  const [evidencePlanActionToDestroy, setEvidencePlanActionToDestroy] =
    useState()
  const [daiAnalysisRenderKey, setDaiAnalysisRenderKey] = useState(0)
  const [startDaiAnalysis, setStartDaiAnalysis] = useState(false)

  const lastEvidence = useRef()
  const scrolledToAnchor = useRef(false)

  // derivado consumido pelo hook abaixo
  const minDate = moment(activity?.governanceProject?.startDate)
  const maxDate = moment(activity?.governanceProject?.endDate)

  const formMethods = useForm({
    validationSchema: schema({
      minDate,
      maxDate,
      planActionsOpened,
      activityDeadlineDate,
      selectedAlternative,
    }),
    defaultValues: defaultValues,
  })

  const {
    control,
    errors,
    handleSubmit,
    watch,
    reset,
    getValues,
    triggerValidation,
    formState: { dirty },
  } = formMethods
  const { fields: evidencePlanActions, append: appendEvidencePlanAction } =
    useFieldArray({ control, name: 'evidencePlanActions' })

  const { user } = useAuth()
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { hash } = useLocation()
  const { handleNewChat } = useDaiChat()
  const {
    assignmentRole,
    isManager,
    isProjectResponsible,
    isAreaResponsible,
    isActivityResponsible,
    isPlanActionResponsible,
  } = useAssignmentPermission({
    governanceProject: activity?.governanceProject,
  })

  const answer = watch('alternativeAnswerId')
  const getDeadlineDate = getValues('deadlineDate')

  const isCoveredByReview = !!activity?.coveredByReview
  // A Revisão nasce no envio; só a cobertura de controles já em awaiting_approval a abre assincronamente (sem `review`).
  const isInReview = helpers.governanceProject.isActivityInReview(activity)
  // Em projeto coberto o gestor não aprova pela plataforma: o fluxo legado de aprovação não se aplica.
  const isAwaitingApproval =
    helpers.governanceProject.isActivityAwaitingApproval(activity)
  const isCompleted = activity?.status === COMPLETED_STATUS_ID
  const isRiskAssumed = activity?.status === RISK_ASSUMED_STATUS_ID
  const isRejectedInReview =
    isCoveredByReview && activity?.status === REJECTED_STATUS_ID
  const hasReviewOpinion =
    helpers.governanceProject.hasSentReviewOpinion(activity)
  const submitDestinationLabel = isCoveredByReview ? 'revisão' : 'aprovação'
  const submitActionLabel = isAwaitingApproval
    ? 'Aprovar atividade'
    : `Enviar para ${submitDestinationLabel}`
  const isNotApplicable = answer === NOT_APPLICABLE_VALUE

  const isInactiveProject =
    activity?.governanceProject?.status === INACTIVE_STATUS_ID
  // true também quando o projeto pai está arquivado, não só concluído
  const isCompletedProject = helpers.governanceProject.isProjectLocked(
    activity?.governanceProject?.status,
  )
  const isArchivedProject =
    activity?.governanceProject?.status === ARCHIVED_STATUS_ID

  const canEditActivity =
    isManager ||
    isProjectResponsible ||
    isAreaResponsible ||
    isActivityResponsible

  const disabledAll =
    isCompleted || isRiskAssumed || isInactiveProject || isCompletedProject
  // Responsável e prazo seguem editáveis durante a revisão; o resto do controle trava.
  const disabledAssignmentFields =
    disabledAll || !canEditActivity || isAwaitingApproval
  const disabledActivityFields = disabledAssignmentFields || isInReview
  const disabledEvidenceFields = disabledAll || isAwaitingApproval || isInReview

  const question = activity?.governanceProjectQuestion
  const videoUrl = helpers.formatters.vimeoEmbedUrl(question?.videoUrl)

  const questionRadioFields = [
    ...(question?.questionAlternatives?.map((item) => ({
      label: item?.title,
      value: item?.id?.toString(),
    })) ?? []),
    { label: 'Não se aplica', value: NOT_APPLICABLE_VALUE },
  ]
  const isInternalUser =
    user?.email?.endsWith('@dponet.com.br') ||
    user?.email?.endsWith('@leavening.com')

  // Configurações de IA do template de projeto de governança, por tipo
  const aiSettings = activity?.governanceProjectTemplateAiSettings
  const chatEnabled = !!aiSettings?.chat?.enabled
  const evidenceAnalysisEnabled = !!aiSettings?.pgnppdEvidenceAnalysis?.enabled

  const trackReviewEvent = (eventId) =>
    helpers.mixpanel.track('Controle de governança', eventId, {
      governanceProjectId,
      activityId: activity?.id,
    })

  const handleOpenSubmitReviewDialog = () => {
    if (!answer) {
      snackbar.open({
        message:
          'É necessário responder a pergunta antes de enviar o controle para revisão.',
        variant: 'error',
      })
      return
    }

    trackReviewEvent('enviar-aprovacao-controle')
    setIsSubmitReviewDialogOpen(true)
  }

  const handleClickSubmitButton = () =>
    isCoveredByReview ? handleOpenSubmitReviewDialog() : handleOpenDialog(false)

  const handleOpenReopenReviewDialog = () => {
    trackReviewEvent('reabrir-controle')
    setIsReopenReviewDialogOpen(true)
  }

  const runReviewRequest = async ({
    sendRequest,
    trackingEventId,
    successMessage,
    setDialogOpen,
  }) => {
    try {
      setLoading(true)
      if (trackingEventId) trackReviewEvent(trackingEventId)

      await sendRequest()

      setDialogOpen?.(false)
      snackbar.open({ message: successMessage, variant: 'success' })
      refresh()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })

      // Conflito (ex.: o parecer acabou de voltar): recarrega o controle no estado novo.
      if (error?.response?.status === constants.errors.HTTP_CONFLICT_STATUS)
        refresh()
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitReview = () =>
    runReviewRequest({
      sendRequest: () =>
        service.dponet.governanceProjectActivity.changeStatus({
          governanceProjectId,
          id: activity?.id,
          status: AWAITING_APPROVAL_STATUS_ID,
        }),
      trackingEventId: 'confirmar-envio-aprovacao-controle',
      successMessage: 'Controle enviado para revisão com sucesso!',
      setDialogOpen: setIsSubmitReviewDialogOpen,
    })

  const handleReopenReview = () =>
    runReviewRequest({
      sendRequest: () =>
        service.dponet.governanceProjectActivity.reopen({
          governanceProjectId,
          id: activity?.id,
        }),
      trackingEventId: 'confirmar-reabrir-controle',
      successMessage: 'Controle reaberto com sucesso!',
      setDialogOpen: setIsReopenReviewDialogOpen,
    })

  // Em revisão só responsável e prazo mudam, por rota dedicada, e nunca o status.
  const handleSaveAssignmentInReview = async () => {
    const isValid = await triggerValidation(['responsible', 'deadlineDate'])

    if (!isValid) return

    const { responsible, deadlineDate } = getValues()

    runReviewRequest({
      sendRequest: () =>
        service.dponet.governanceProjectActivity.updateAssignment({
          governanceProjectId,
          id: activity?.id,
          responsibleId: responsible?.id,
          ...(!!deadlineDate && {
            deadlineDate: moment(deadlineDate).toISOString(),
          }),
        }),
      successMessage: 'Atividade atualizada com sucesso!',
    })
  }

  const handleOpenDialog = (reject) => {
    setIsReject(!!reject)
    setOpenDialog((prevState) => !prevState)
  }

  // createdManually é a única marca confiável aqui: `evidence.id` não é um campo registrado no
  // formulário e por isso nunca chega em `data` — a condição anterior (`!action?.evidence?.id`) era
  // sempre verdadeira e mandava nome e descrição também da evidência exigida pelo template.
  const isManualEvidence = (action) => !!action?.createdManually

  const onSubmit = async (data) => {
    try {
      setLoading(true)

      const activityData = {
        alternativeAnswerId: isNotApplicable ? null : data?.alternativeAnswerId,
        deadlineDate: moment(data?.deadlineDate).toISOString(),
        responsibleId: data?.responsible?.id,
        ...(!!data?.alternativeAnswerId && {
          notApplicable: isNotApplicable,
          // Reprovado em projeto coberto continua Reprovado ao salvar.
          ...(!isRejectedInReview && { status: IN_PROGRESS_STATUS_ID }),
          ...(isNotApplicable
            ? {
                exceptionReason: data?.exceptionReason,
                applicabilityReason: data?.applicabilityReason,
                observations: null,
              }
            : {
                observations: data?.observations,
                exceptionReason: null,
                applicabilityReason: null,
              }),
        }),
      }
      const evidencePlanActionsData = data?.evidencePlanActions?.map(
        (action) => ({
          id: action?.originId,
          evidence: {
            // Nome e descrição só viajam para a evidência criada manualmente — a exigida pelo template
            // tem esses campos definidos pelo modelo e não deve aceitar sobrescrita vinda do payload.
            ...(isManualEvidence(action) && {
              title: action?.evidence?.title,
              description: action?.evidence?.description,
            }),
            justify: action?.evidence?.justify,
            attachments: action?.evidence?.attachments,
            privacyPolicyIds: action?.evidence?.privacyPolicyIds?.map(
              (item) => item?.id,
            ),
          },
          planActions: action?.planActions?.map((plan) => ({
            id: plan?.originId,
            responsibleUserId: plan?.responsible?.id,
            deadlineDate: moment(plan?.deadlineDate).toISOString(),
            what: plan?.what,
            how: plan?.how,
            because: plan?.because,
            where: plan?.where,
            howPrice: plan?.howPrice,
            status: !!plan?.status
              ? planActionsOpened.includes(plan?.originId)
                ? HEADWAY_STATUS_ID
                : plan?.status
              : INITIAL_STATUS_ID,
          })),
        }),
      )

      if (
        size(evidencePlanActionsData) > 0 &&
        (canEditActivity || isPlanActionResponsible) &&
        !isNotApplicable
      ) {
        await service.dponet.governanceProject.answerPlanAction({
          governanceProjectId,
          governanceProjectActivityId: activity?.id,
          evidencePlanActions: evidencePlanActionsData,
        })
      }

      if (canEditActivity) {
        await service.dponet.governanceProjectActivity.answer({
          governanceProjectId,
          id: activity?.id,
          ...activityData,
        })
      }

      snackbar.open({
        message: 'Atividade atualizada com sucesso!',
        variant: 'success',
      })
      reset()
      refresh()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAddEvidencePlanAction = () => {
    appendEvidencePlanAction({
      createdManually: true,
      evidence: {
        id: null,
        title: '',
        description: '',
        justify: '',
        attachments: [],
      },
      planActions: [],
    })

    setTimeout(() => {
      if (lastEvidence?.current)
        lastEvidence.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        })
    }, 100)
  }

  const handleCloseDestroyEvidencePlanAction = () => {
    setOpenDestroyEvidencePlanAction(false)
    setEvidencePlanActionToDestroy()
  }

  const removeEvidencePlanAction = (register) => {
    const newEvidencePlanActions = filter(
      evidencePlanActions,
      (item) => item?.id !== register?.id,
    )
    reset({
      ...getValues(),
      evidencePlanActions: newEvidencePlanActions,
    })
  }

  const handleDestroyEvidencePlanAction = async () => {
    try {
      setLoading(true)

      await service.dponet.governanceProject.destroyEvidencePlanAction({
        governanceProjectId,
        evidencePlanActionId: evidencePlanActionToDestroy?.id,
      })

      snackbar.open({
        message: 'Evidência excluída com sucesso!',
        variant: 'success',
      })

      removeEvidencePlanAction(evidencePlanActionToDestroy)
      handleCloseDestroyEvidencePlanAction()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveEvidencePlanAction = async (id) => {
    if (disabledActivityFields) return
    const register = evidencePlanActions.find((item) => item?.id === id)

    if (!!Number(register?.id)) {
      setEvidencePlanActionToDestroy(register)
      return setOpenDestroyEvidencePlanAction(true)
    }

    removeEvidencePlanAction(register)
  }

  const handleClickDaiButton = () => {
    setDaiAnalysisRenderKey((currentRenderKey) => currentRenderKey + 1)
    setStartDaiAnalysis(true)
  }

  const handleOpenGovernanceChat = () => {
    handleNewChat({
      agent: constants.dai.agents.GOVERNANCE_PROJECT_COPILOT_AGENT,
      context: {
        governanceProjectId: String(governanceProjectId),
        governanceProjectActivityId: String(activity?.id),
        projectTemplateName: activity?.governanceProject?.name ?? '',
      },
    })
  }

  useEffect(() => {
    setActivityDeadlineDate(getDeadlineDate)
  }, [getDeadlineDate])

  useEffect(() => {
    setSelectedAlternative(
      question?.questionAlternatives?.find(
        (item) => item.id.toString() === answer,
      ),
    )
  }, [answer])

  // Sair da atividade com o formulário sujo descarta tudo o que não foi salvo, inclusive arquivos já
  // escolhidos e ainda não enviados. O <Prompt> cobre qualquer transição do react-router — botão Voltar
  // da tela, voltar do navegador, links, redirecionamentos — e o beforeunload cobre o que o Prompt não
  // alcança: recarregar a página, fechar a aba ou ir para uma URL externa.
  useEffect(() => {
    if (!dirty) return

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      // Navegadores modernos ignoram a mensagem customizada e exibem o texto padrão deles, mas
      // `returnValue` precisa ser preenchido para o diálogo aparecer.
      event.returnValue = LEAVE_WITH_PENDING_CHANGES_MESSAGE
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty])

  // Âncora vinda da listagem de controles do documento: a atividade pode ter várias evidências, e o
  // hash aponta para a que originou o vínculo. O timeout segue o mesmo padrão do scroll ao adicionar
  // uma evidência — os campos são montados pelo useFieldArray um tick depois de a atividade carregar.
  // O ref garante que a rolagem aconteça só na chegada, e não a cada re-render do formulário.
  useEffect(() => {
    const anchor = hash?.replace('#', '')

    if (!anchor || scrolledToAnchor.current || isEmpty(evidencePlanActions)) {
      return
    }

    const timeout = setTimeout(() => {
      const element = document.getElementById(anchor)

      if (!element) return

      scrolledToAnchor.current = true
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 300)

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, evidencePlanActions])

  return (
    <Fragment>
      <Prompt when={dirty} message={LEAVE_WITH_PENDING_CHANGES_MESSAGE} />
      <FormContext {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="flex"
            flexDirection="column"
            gridGap={theme.spacing(3)}
            textAlign="justify"
          >
            <Paper variant="outlined" className={classes.sectionPaper}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h5" gutterBottom>
                    Responsável pela atividade
                  </Typography>
                  <Controller
                    as={
                      <Autocomplete
                        options={users}
                        getOptionLabel={(option) => option.name || ''}
                        getOptionSelected={(option, value) =>
                          option.id === value.id || value.id === 0
                        }
                        disabled={disabledAssignmentFields}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            disabled={disabledAssignmentFields}
                            error={!!errors.responsible}
                            helperText={
                              errors?.responsible?.message ||
                              errors?.responsible?.id?.message
                            }
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <UserIcon
                                    color={theme.palette.secondary.main}
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    }
                    control={control}
                    name="responsible"
                    mode="onChange"
                    onChange={([, data]) => data}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h5" gutterBottom>
                    Prazo da atividade *
                  </Typography>
                  <Controller
                    as={
                      <KeyboardDatePicker
                        inputVariant="outlined"
                        format="DD/MM/yyyy"
                        error={!!errors.deadlineDate}
                        helperText={errors?.deadlineDate?.message}
                        onChange={(newValue) => ({ value: newValue })}
                        disablePast
                        minDate={minDate}
                        maxDate={maxDate}
                        disabled={disabledAssignmentFields}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <WatchIcon color={theme.palette.secondary.main} />
                            </InputAdornment>
                          ),
                        }}
                        onClick={handleOpenAlertDialog}
                      />
                    }
                    control={control}
                    name="deadlineDate"
                    mode="onChange"
                  />
                </Grid>
              </Grid>
            </Paper>

            {chatEnabled && (
              <Box mt={1}>
                <DaiGovernanceBanner onOpenChat={handleOpenGovernanceChat} />
              </Box>
            )}

            <Paper variant="outlined" className={classes.sectionPaper}>
              <Box
                display="flex"
                flexDirection="column"
                gridGap={theme.spacing(2)}
              >
                <Grid container spacing={3}>
                  <Grid
                    item
                    xs={12}
                    xl={!!videoUrl ? 6 : 12}
                    className={classes.codeConductContainer}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography
                        variant="h4"
                        className={classes.themeBreakLines}
                      >
                        {activityName}
                      </Typography>
                      {question?.purpose && (
                        <WhiteTooltip title={question.purpose} arrow>
                          <InfoIcon className={classes.infoIcon} />
                        </WhiteTooltip>
                      )}
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      className={classes.whiteSpaces}
                    >
                      {question?.question}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="secondary"
                      gutterBottom
                      className={classes.whiteSpaces}
                    >
                      {question?.explanation}
                    </Typography>
                    <Box>
                      <Typography variant="h6" color="secondary" gutterBottom>
                        Evidências exigidas:
                      </Typography>
                      <ol className={classes.orderedList}>
                        {filter(activity?.evidencePlanActions, {
                          createdManually: false,
                        })?.map((item) => (
                          <li key={item?.evidence?.id}>
                            {item?.evidence?.title};
                          </li>
                        ))}
                      </ol>
                    </Box>
                    <Box>
                      <Controller
                        as={
                          <RadioGroup
                            className={clsx(classes.radio, {
                              [classes.radioError]: !!errors.answer,
                            })}
                          >
                            {questionRadioFields?.map((field) => (
                              <FormControlLabel
                                disabled={disabledActivityFields}
                                key={field?.value}
                                value={field?.value}
                                control={
                                  <Radio
                                    className={clsx({
                                      [classes.radioError]: !!errors.answer,
                                    })}
                                  />
                                }
                                label={field?.label}
                              />
                            ))}
                          </RadioGroup>
                        }
                        control={control}
                        name="alternativeAnswerId"
                      />
                      {!!errors?.answer && (
                        <FormHelperText error={!!errors.answer}>
                          {<>{errors?.answer?.message}</>}
                        </FormHelperText>
                      )}
                    </Box>
                  </Grid>
                  {!!videoUrl && (
                    <Grid item xs={12} xl={6}>
                      <Box
                        height="100%"
                        width="100%"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <iframe
                          className={classes.videoPlayer}
                          src={videoUrl}
                          title={activity?.governanceProjectQuestion.theme}
                          allow="fullscreen; picture-in-picture; clipboard-write"
                          frameBorder="0"
                          allowFullScreen
                        />
                      </Box>
                    </Grid>
                  )}
                  {selectedAlternative?.observationsRequired &&
                    !isNotApplicable && (
                      <Grid item xs={12}>
                        <Controller
                          as={
                            <TextField
                              variant="outlined"
                              label="Observações *"
                              fullWidth
                              multiline
                              minRows={4}
                              disabled={disabledActivityFields}
                              error={!!errors.observations}
                              helperText={errors?.observations?.message}
                            />
                          }
                          name="observations"
                          control={control}
                        />
                      </Grid>
                    )}
                  {isNotApplicable && (
                    <Grid item xs={12}>
                      <Controller
                        as={
                          <TextField
                            variant="outlined"
                            label="Justificativa de exclusão *"
                            fullWidth
                            multiline
                            minRows={4}
                            disabled={disabledActivityFields}
                            error={!!errors.exceptionReason}
                            helperText={errors?.exceptionReason?.message}
                          />
                        }
                        name="exceptionReason"
                        control={control}
                      />
                    </Grid>
                  )}
                  {isNotApplicable && (
                    <Grid item xs={12}>
                      <Controller
                        as={
                          <TextField
                            variant="outlined"
                            label="Justificativa de aplicabilidade *"
                            fullWidth
                            multiline
                            minRows={4}
                            disabled={disabledActivityFields}
                            error={!!errors.applicabilityReason}
                            helperText={errors?.applicabilityReason?.message}
                          />
                        }
                        name="applicabilityReason"
                        control={control}
                      />
                    </Grid>
                  )}

                  {!!activity?.rejectionReason && !hasReviewOpinion && (
                    <Grid item xs={12}>
                      <Alert severity="error" variant="standard" icon={false}>
                        <AlertTitle>Motivo da reprovação:</AlertTitle>
                        <JoditEditor.Preview
                          sanitizeProfile={RICH_TEXT_SANITIZE_PROFILE}
                          content={
                            helpers.governanceProject.formatOpinionHtml(
                              activity?.rejectionReason,
                            ) || 'Nenhum motivo informado.'
                          }
                        />
                      </Alert>
                    </Grid>
                  )}
                </Grid>

                {!!activity?.approvalComment && (
                  <Alert severity="info" variant="standard" icon={false}>
                    <AlertTitle>Comentário de aprovação:</AlertTitle>
                    <Typography
                      variant="body2"
                      className={classes.formattingText}
                    >
                      {activity?.approvalComment ||
                        'Nenhum comentário informado.'}
                    </Typography>
                  </Alert>
                )}
                <Fragment>
                  <Box
                    width="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="h4">Evidências</Typography>
                    <Box
                      display="flex"
                      alignItems="center"
                      gridGap={theme.spacing(2)}
                    >
                      {isInternalUser &&
                        !isArchivedProject &&
                        evidenceAnalysisEnabled && (
                          <ActivityAnalysisDaiButton
                            onClick={handleClickDaiButton}
                            disabled={startDaiAnalysis}
                          />
                        )}
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddEvidencePlanAction}
                        disabled={disabledActivityFields}
                      >
                        Nova evidência
                      </Button>
                    </Box>
                  </Box>

                  {isInternalUser && evidenceAnalysisEnabled && (
                    <ActivityAnalysisDai
                      key={daiAnalysisRenderKey}
                      governanceProjectId={governanceProjectId}
                      governanceProjectActivity={activity}
                      startDaiAnalysis={startDaiAnalysis}
                      setStartDaiAnalysis={setStartDaiAnalysis}
                      refresh={refresh}
                    />
                  )}
                  {evidencePlanActions?.map((evidencePlanAction, index) => {
                    const defaultEvidence = find(
                      activity?.evidencePlanActions,
                      {
                        id:
                          Number(evidencePlanAction?.id) ||
                          evidencePlanAction?.originId,
                      },
                    )

                    return (
                      <EvidencePlanAction
                        key={evidencePlanAction?.id}
                        ref={
                          index === size(evidencePlanActions) - 1
                            ? lastEvidence
                            : null
                        }
                        removeEvidencePlanAction={() =>
                          handleRemoveEvidencePlanAction(evidencePlanAction?.id)
                        }
                        defaultEvidencePlanAction={defaultEvidence}
                        isCreatedManually={
                          !defaultEvidence || defaultEvidence?.createdManually
                        }
                        activity={activity}
                        canEditActivity={canEditActivity}
                        componentIndex={index}
                        disabledAll={disabledEvidenceFields}
                        evidencePlanAction={evidencePlanAction}
                        governanceProjectId={governanceProjectId}
                        planActionsOpened={planActionsOpened}
                        setLoading={setLoading}
                        setStates={setStates}
                        users={users}
                      />
                    )
                  })}
                </Fragment>
              </Box>
            </Paper>

            {!disabledAll && isInReview && (
              <Box
                display="flex"
                justifyContent="flex-end"
                gridGap={theme.spacing(1)}
              >
                <PermittedByAssignmentRole
                  unpermittedRoles={[
                    PLAN_ACTION_RESPONSIBLE_ROLE,
                    OBSERVER_ROLE,
                  ]}
                  userRole={assignmentRole}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveAssignmentInReview}
                    disabled={!dirty}
                  >
                    Salvar
                  </Button>
                  <Tooltip
                    title={
                      dirty
                        ? 'Salve as alterações antes de reabrir o controle!'
                        : ''
                    }
                  >
                    <Box>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleOpenReopenReviewDialog}
                        disabled={dirty}
                      >
                        Reabrir controle
                      </Button>
                    </Box>
                  </Tooltip>
                </PermittedByAssignmentRole>
              </Box>
            )}

            {!disabledAll && !isInReview && (
              <Box
                display="flex"
                justifyContent="flex-end"
                gridGap={theme.spacing(1)}
              >
                <PermittedByAssignmentRole
                  unpermittedRoles={[OBSERVER_ROLE]}
                  userRole={assignmentRole}
                >
                  <PermittedByAssignmentRole
                    userRole={assignmentRole}
                    {...(isAwaitingApproval
                      ? {
                          permittedRoles: [
                            MANAGER_ROLE,
                            PROJECT_RESPONSIBLE_ROLE,
                          ],
                        }
                      : {
                          unpermittedRoles: [OBSERVER_ROLE],
                        })}
                  >
                    {isAwaitingApproval ? (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleOpenDialog(true)}
                        disabled={dirty}
                      >
                        Reprovar
                      </Button>
                    ) : (
                      <Button variant="contained" color="primary" type="submit">
                        Salvar
                      </Button>
                    )}
                  </PermittedByAssignmentRole>
                  <PermittedByAssignmentRole
                    unpermittedRoles={[
                      PLAN_ACTION_RESPONSIBLE_ROLE,
                      OBSERVER_ROLE,
                    ]}
                    userRole={assignmentRole}
                  >
                    <Tooltip
                      title={
                        dirty
                          ? `Salve as alterações antes de tentar ${
                              isAwaitingApproval
                                ? 'aprovar'
                                : `enviar para ${submitDestinationLabel}`
                            } a atividade!`
                          : ''
                      }
                    >
                      <Box>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleClickSubmitButton}
                          disabled={dirty}
                        >
                          {submitActionLabel}
                        </Button>
                      </Box>
                    </Tooltip>
                  </PermittedByAssignmentRole>
                </PermittedByAssignmentRole>
              </Box>
            )}
          </Box>
        </form>
        <AlertDialog
          open={openDestroyEvidencePlanAction}
          onClose={handleCloseDestroyEvidencePlanAction}
          variant="standard"
          severity="warning"
          content="Tem certeza de que deseja excluir esta evidência? Essa ação não poderá ser desfeita!"
          dialogActions={
            <>
              <Button
                variant="outlined"
                color="primary"
                className={classes.outlinedWarningButton}
                onClick={handleCloseDestroyEvidencePlanAction}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={classes.containerWarningButton}
                onClick={handleDestroyEvidencePlanAction}
              >
                Excluir
              </Button>
            </>
          }
        />
      </FormContext>
      <ConfirmationDialog
        open={isSubmitReviewDialogOpen}
        loading={loading}
        setOpen={setIsSubmitReviewDialogOpen}
        actionAcceptButton={handleSubmitReview}
        textButton="Enviar"
        textButtonCancel="Cancelar"
        hiddenIcon
        text={
          <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
            <Typography variant="h3">Enviar para revisão</Typography>
            <Typography>
              {`Ao enviar, o controle ${activityCode} seguirá para revisão e não poderá ser editado até a conclusão da análise. Se precisar ajustar algo, você poderá reabrir o controle enquanto ele não for analisado.`}
            </Typography>
          </Box>
        }
      />
      <ConfirmationDialog
        open={isReopenReviewDialogOpen}
        loading={loading}
        setOpen={setIsReopenReviewDialogOpen}
        actionAcceptButton={handleReopenReview}
        textButton="Reabrir"
        textButtonCancel="Cancelar"
        hiddenIcon
        text={
          <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
            <Typography variant="h3">Reabrir controle</Typography>
            <Typography>
              {`O controle ${activityCode} sairá da revisão e voltará a ficar editável. Para ser analisado, ele precisará ser enviado para revisão novamente.`}
            </Typography>
          </Box>
        }
      />
      <CompleteModal
        open={openDialog}
        setOpen={setOpenDialog}
        setLoading={setLoading}
        activity={activity}
        answer={answer}
        governanceProjectId={governanceProjectId}
        refresh={refresh}
        reject={isReject}
      />
    </Fragment>
  )
}

ActivityManageForm.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object),
  activity: PropTypes.object,
  governanceProjectId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  refresh: PropTypes.func.isRequired,
  states: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    defaultValues: PropTypes.object,
    planActionsOpened: PropTypes.array.isRequired,
  }),
  setStates: PropTypes.shape({
    setLoading: PropTypes.func.isRequired,
    setPlanActionsOpened: PropTypes.func.isRequired,
    setDefaultValues: PropTypes.func.isRequired,
    setModifiedForm: PropTypes.func.isRequired,
    handleOpenAlertDialog: PropTypes.func.isRequired,
  }),
  activityName: PropTypes.string.isRequired,
  activityCode: PropTypes.string.isRequired,
}

export default ActivityManageForm
